import React, { useState, useEffect } from 'react';
import { X, Download, Printer, CaretLeft, CaretRight, SealCheck, CircleNotch, NotePencil, ArrowUUpLeft, ClockCounterClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import gradesService from '../services/gradesService';
import DialogModal from './DialogModal';
import ReportCardRemarksModal from './ReportCardRemarksModal';
import './ReportCardPreviewModal.css';

/**
 * ReportCardPreviewModal Component
 * Displays a report card in an HTML preview modal with options to print or download as PDF
 * Supports both single and bulk report preview modes
 */
function ReportCardPreviewModal({
  isOpen,
  onClose,
  html,
  studentName,
  academicTerm,
  studentId,
  onDownloadComplete,
  bulkMode = false,
  bulkReportStudents = [],
  currentReportIndex = 0,
  totalReports = 0,
  onNextReport,
  onPreviousReport,
  onJumpToStudent,
  onDownloadAll,
  onApproveAll,
  status,
  canApprove = false,
  onApproved,
  onRecalled,
  reportData,
  canEditRemarks = false,
  onRemarksSaved
}) {
  const [downloading, setDownloading] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approvingAll, setApprovingAll] = useState(false);
  const [recalling, setRecalling] = useState(false);
  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [recallHistory, setRecallHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    primaryAction: null,
    secondaryAction: null
  });

  // Correction history — admin-only, single-report mode only. Re-fetched
  // whenever a recall/approve changes status, so it stays in sync without
  // needing its own separate refresh trigger.
  useEffect(() => {
    if (!isOpen || bulkMode || !canApprove || !studentId || !academicTerm) {
      setRecallHistory([]);
      return;
    }
    gradesService.getReportCardRecallHistory(studentId, academicTerm)
      .then((data) => setRecallHistory(Array.isArray(data) ? data : []))
      .catch(() => setRecallHistory([]));
  }, [isOpen, bulkMode, canApprove, studentId, academicTerm, status]);

  if (!isOpen) return null;

  const showDialog = (config) => {
    setDialogConfig({
      isOpen: true,
      ...config
    });
  };

  const closeDialog = () => {
    setDialogConfig(prev => ({
      ...prev,
      isOpen: false
    }));
  };

  const handlePrint = () => {
    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        showDialog({
          title: 'Pop-ups Blocked',
          message: 'Please allow pop-ups to print the report card. Check your browser settings and try again.',
          type: 'warning',
          primaryAction: {
            label: 'Got it',
            onClick: closeDialog
          }
        });
        return;
      }
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Report Card - ${studentName}</title>
          <style>
            body {
              margin: 0;
              padding: 10px;
              font-family: Arial, sans-serif;
            }
            @media print {
              body { margin: 0; padding: 10px; }
              .no-print { display: none !important; }
            }
          </style>
        </head>
        <body>
          ${html}
          <script>
            window.onload = function() {
              window.print();
            }
          </script>
        </body>
        </html>
      `);
      printWindow.document.close();
    } catch (error) {
      console.error('Error printing:', error);
      showDialog({
        title: 'Print Error',
        message: 'Failed to open print dialog. Please try again.',
        type: 'error',
        primaryAction: {
          label: 'OK',
          onClick: closeDialog
        }
      });
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await gradesService.downloadReportCard(studentId, academicTerm);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `reportcard_${studentId}_${academicTerm}.pdf`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      if (onDownloadComplete) {
        onDownloadComplete();
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showDialog({
        title: 'Download Error',
        message: error.message || 'Failed to download report card. Please try again.',
        type: 'error',
        primaryAction: {
          label: 'OK',
          onClick: closeDialog
        }
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    try {
      await onDownloadAll();
    } catch (error) {
      console.error('Error downloading all reports:', error);
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      await gradesService.approveReportCard(studentId, academicTerm);
      toast.success('Report card approved — now visible to the student/parent');
      if (onApproved) onApproved();
    } catch (error) {
      showDialog({
        title: 'Approval Failed',
        message: error.message || 'Failed to approve the report card. Please try again.',
        type: 'error',
        primaryAction: { label: 'OK', onClick: closeDialog }
      });
    } finally {
      setApproving(false);
    }
  };

  const handleRecall = () => {
    showDialog({
      title: 'Recall Report Card',
      message: `This will withdraw ${studentName}'s report card from the parent/student view and notify the parent it's being corrected. Why is it being recalled?`,
      type: 'warning',
      input: { label: 'Reason', placeholder: 'e.g. Wrong exam score for Mathematics', required: true },
      primaryAction: {
        label: 'Recall',
        onClick: async (reason) => {
          setRecalling(true);
          try {
            await gradesService.recallReportCard(studentId, academicTerm, reason);
            toast.success('Report card recalled — parent has been notified');
            if (onRecalled) onRecalled();
          } catch (error) {
            showDialog({
              title: 'Recall Failed',
              message: error.message || 'Failed to recall the report card. Please try again.',
              type: 'error',
              primaryAction: { label: 'OK', onClick: closeDialog }
            });
          } finally {
            setRecalling(false);
          }
        }
      },
      secondaryAction: { label: 'Cancel', onClick: () => {} }
    });
  };

  const handleApproveAll = async () => {
    setApprovingAll(true);
    try {
      await onApproveAll();
    } catch (error) {
      showDialog({
        title: 'Bulk Approval Failed',
        message: error.message || 'Failed to approve report cards. Please try again.',
        type: 'error',
        primaryAction: { label: 'OK', onClick: closeDialog }
      });
    } finally {
      setApprovingAll(false);
    }
  };

  const handleStudentChange = (e) => {
    const index = parseInt(e.target.value);
    if (onJumpToStudent) {
      onJumpToStudent(index);
    }
  };

  return (
    <>
      <div className="preview-modal-overlay" onClick={onClose}>
        <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="preview-modal-header">
            <div className="preview-modal-title-section">
              {bulkMode && bulkReportStudents.length > 0 ? (
                <>
                  <div className="bulk-nav-select">
                    <label>Student:</label>
                    <select 
                      value={currentReportIndex} 
                      onChange={handleStudentChange}
                      className="bulk-student-select"
                    >
                      {bulkReportStudents.map((student, index) => (
                        <option key={index} value={index}>
                          {student.student_name} ({index + 1} of {totalReports})
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="preview-modal-subtitle">{academicTerm} • Student {currentReportIndex + 1} of {totalReports}</p>
                </>
              ) : (
                <>
                  <h2>
                    {studentName}
                    {status && (
                      <span className={`report-card-status-badge status-${status}`}>
                        {status === 'approved' ? 'Approved' : 'Draft'}
                      </span>
                    )}
                  </h2>
                  <p className="preview-modal-subtitle">{academicTerm}</p>
                </>
              )}
            </div>
            <button
              className="preview-close-btn"
              onClick={onClose}
              aria-label="Close modal"
              title="Close"
            >
              <X size={24} />
            </button>
          </div>

          {/* Toolbar */}
          <div className="preview-toolbar">
            {bulkMode && bulkReportStudents.length > 0 && (
              <div className="bulk-nav-buttons">
                <button 
                  onClick={onPreviousReport} 
                  className="preview-toolbar-btn preview-btn-secondary"
                  disabled={currentReportIndex === 0}
                  title="Previous student"
                >
                  <CaretLeft size={18} />
                  <span>Previous</span>
                </button>
                <button 
                  onClick={onNextReport} 
                  className="preview-toolbar-btn preview-btn-secondary"
                  disabled={currentReportIndex === totalReports - 1}
                  title="Next student"
                >
                  <span>Next</span>
                  <CaretRight size={18} />
                </button>
              </div>
            )}
            
            <div className="toolbar-actions">
              {!bulkMode && canEditRemarks && (
                <button
                  onClick={() => setShowRemarksModal(true)}
                  className="preview-toolbar-btn preview-btn-secondary"
                  title="Edit remarks, attitude, conduct, interest, and term dates"
                >
                  <NotePencil size={18} />
                  <span>Edit Remarks</span>
                </button>
              )}
              {!bulkMode && canApprove && status === 'draft' && (
                <button
                  onClick={handleApprove}
                  className="preview-toolbar-btn preview-btn-approve"
                  disabled={approving}
                  title="Approve this report card so the parent/student can see it"
                >
                  {approving ? <CircleNotch size={18} className="spinner" /> : <SealCheck size={18} weight="bold" />}
                  <span>{approving ? 'Approving...' : 'Approve'}</span>
                </button>
              )}
              {!bulkMode && canApprove && status === 'approved' && (
                <button
                  onClick={handleRecall}
                  className="preview-toolbar-btn preview-btn-secondary"
                  disabled={recalling}
                  title="Withdraw this report card for correction — hides it from the parent/student until re-approved"
                >
                  {recalling ? <CircleNotch size={18} className="spinner" /> : <ArrowUUpLeft size={18} weight="bold" />}
                  <span>{recalling ? 'Recalling...' : 'Recall'}</span>
                </button>
              )}
              <button
                onClick={handlePrint}
                className="preview-toolbar-btn preview-btn-secondary"
                disabled={downloading || downloadingAll}
                title="Print report card"
              >
                <Printer size={18} />
                <span>Print</span>
              </button>
              <button
                onClick={handleDownloadPDF}
                className="preview-toolbar-btn preview-btn-primary"
                disabled={downloading || downloadingAll}
                title="Download individual report as PDF"
              >
                <Download size={18} />
                <span>{downloading ? 'Downloading...' : 'Download'}</span>
              </button>

              {bulkMode && bulkReportStudents.length > 0 && canApprove && onApproveAll && (
                <button
                  onClick={handleApproveAll}
                  className="preview-toolbar-btn preview-btn-approve"
                  disabled={approvingAll}
                  title="Approve every draft report card in this class/term so parents/students can see them"
                >
                  {approvingAll ? <CircleNotch size={18} className="spinner" /> : <SealCheck size={18} weight="bold" />}
                  <span>{approvingAll ? 'Approving...' : 'Approve All'}</span>
                </button>
              )}
              {bulkMode && bulkReportStudents.length > 0 && (
                <button
                  onClick={handleDownloadAll}
                  className="preview-toolbar-btn preview-btn-primary"
                  disabled={downloadingAll || downloading}
                  title="Download all reports as ZIP"
                >
                  <Download size={18} />
                  <span>{downloadingAll ? 'Downloading All...' : `Download All (${totalReports})`}</span>
                </button>
              )}
            </div>
          </div>

          {/* Correction History — admin-only, only shown when this report has been recalled before */}
          {!bulkMode && canApprove && recallHistory.length > 0 && (
            <div className="preview-history">
              <button
                type="button"
                className="preview-history-toggle"
                onClick={() => setShowHistory(!showHistory)}
              >
                <ClockCounterClockwise size={16} />
                <span>Correction History ({recallHistory.length})</span>
                <span className="preview-history-caret">{showHistory ? '▲' : '▼'}</span>
              </button>
              {showHistory && (
                <ul className="preview-history-list">
                  {recallHistory.map((h) => (
                    <li key={h.id}>
                      <strong>{h.resent_at ? 'Corrected & resent' : 'Recalled, outstanding'}</strong>
                      {' — '}{h.reason}
                      <br />
                      <span className="preview-history-meta">
                        Recalled {new Date(h.recalled_at).toLocaleString()}
                        {h.resent_at && <> · Resent {new Date(h.resent_at).toLocaleString()}</>}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* HTML Content */}
          <div className="preview-content">
            <div className="preview-inner-content" dangerouslySetInnerHTML={{ __html: html }} />
          </div>
        </div>
      </div>

      {/* Dialog Modal */}
      <DialogModal
        isOpen={dialogConfig.isOpen}
        onClose={closeDialog}
        title={dialogConfig.title}
        message={dialogConfig.message}
        type={dialogConfig.type}
        primaryAction={dialogConfig.primaryAction}
        secondaryAction={dialogConfig.secondaryAction}
      />

      {/* Remarks Modal — narrative fields the auto-generated preview never sets */}
      <ReportCardRemarksModal
        isOpen={showRemarksModal}
        onClose={() => setShowRemarksModal(false)}
        studentId={studentId}
        academicTermId={academicTerm}
        initialData={reportData}
        onSaved={onRemarksSaved}
      />
    </>
  );
}

export default ReportCardPreviewModal;
